/**
 * 자리표시자(미설정) URL 판별 — 출시 전 "채워야 할 것"을 아이에게 들키지 않게 한다.
 *
 * 왜 필요한가: 콘텐츠 파일에는 운영자가 나중에 채울 값이 `REPLACE` 로 들어 있다
 * (스마트스토어 주소·유튜브 영상·신청 폼). 그대로 두면 아이가 버튼을 눌렀을 때
 * **404 나 남의 사이트**로 간다. 링크를 그리기 전에 여기서 걸러 "준비 중"으로 바꾼다.
 *
 * 개발 중 로컬 주소(`localhost:8788`)가 콘텐츠에 섞여 커밋되는 사고도 같은 함수로 막는다 —
 * 운영(https)에서는 로드조차 되지 않고(혼합 콘텐츠) 조용히 빈 이미지가 된다.
 * 단, **로컬에서 개발 중일 때는 로컬 주소가 정상**이므로 그때는 통과시킨다.
 */
const PLACEHOLDER_TOKENS = ["REPLACE", "TODO", "example.com", "youtu.be/xxxx"];
const LOCAL_HOST_RE = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(:\d+)?$/i;

/** 지금 실행 중인 곳이 로컬인가(브라우저 밖이면 false — 빌드·노드 검사에선 항상 걸러낸다).
 *  shared 는 DOM lib 없이 컴파일되므로(엣지·서버 공용) globalThis 로 조회한다. */
function runningLocally(): boolean {
  const host = (globalThis as { location?: { host?: string } }).location?.host;
  return typeof host === "string" && LOCAL_HOST_RE.test(host);
}

/**
 * 아직 채워지지 않은 값인가. true 면 링크·이미지를 그리지 말고 "준비 중"으로 처리한다.
 * @param isLocal 판정 기준 강제(테스트·빌드 검사용). 생략하면 실행 환경으로 판단.
 */
export function isPlaceholderUrl(url: string | undefined | null, isLocal = runningLocally()): boolean {
  const s = (url ?? "").trim();
  if (!s) return true;
  if (PLACEHOLDER_TOKENS.some((t) => s.includes(t))) return true;
  // 로컬 절대주소: 로컬에서 개발 중이면 정상, 운영에 배포되면 깨진 링크다.
  if (!isLocal) {
    if (isLocalHostUrl(s)) return true;
  }
  return false;
}

/**
 * 🏠 **이 주소가 내 컴퓨터를 가리키나.**
 *
 * 🔴 이 판정이 관리자 세 곳에 따로 적혀 있었다(`/localhost|127.0.0.1/`) — 셋 다
 * `0.0.0.0` 과 `[::1]` 을 몰랐다. 제일 위험한 곳이 **QR 인쇄**다: 종이에 찍혀 행사장에서
 * 아이 손에 들어가는데, 「이 주소는 이 컴퓨터에서만 열려요」 경고가 안 뜨면 그대로 인쇄된다.
 * 여기 한 곳에서 정하고, 나중에 형태가 늘어도 같이 늘게 한다.
 *
 * 호스트만 본다 — 경로·쿼리는 상관없다. 주소가 아니면 `false`(모르면 「로컬이다」라고 하지 않는다).
 */
export function isLocalHostUrl(url: string | undefined | null): boolean {
  const s = (url ?? "").trim();
  if (!s) return false;
  try {
    return LOCAL_HOST_RE.test(new URL(s).host);
  } catch {
    return false; // 상대경로 등 — 호스트가 없다
  }
}

/** 채워진 값만 통과. 미설정이면 undefined → 호출부가 버튼을 숨기거나 "준비 중"을 보여준다. */
export function configuredUrl(url: string | undefined | null, isLocal?: boolean): string | undefined {
  return isPlaceholderUrl(url, isLocal) ? undefined : (url ?? "").trim();
}

/** 밖으로 나가는 버튼의 상태. */
export interface LinkAction {
  /** 누를 수 있는가 — **주소가 있을 때만** true. */
  enabled: boolean;
  /** 버튼에 쓸 말 — 못 누를 땐 왜 못 누르는지 말해 준다. */
  label: string;
}

/**
 * 외부 링크 버튼을 어떻게 그릴지.
 *
 * **지키려는 규칙: "눌리는데 아무 일도 안 하는 버튼"이 없어야 한다.**
 * 실제로 체험공간의 링크 오브젝트가 그랬다 — 주소가 아직 `REPLACE` 라
 * `configuredUrl` 이 undefined 로 걸렀는데, 버튼은 "이동하기" 그대로 떠 있어서
 * 아이가 눌러도 아무 일이 없었다. 왜 안 되는지 알 수가 없다.
 *
 * 비활성만 시키고 문구를 그대로 두는 것도 같은 문제다("스토어로 보러가기"인데 안 눌림).
 * 그래서 **활성 여부와 문구를 한 곳에서 같이** 정한다.
 */
export function linkAction(
  url: string | undefined | null,
  ready: string,
  pending: string,
  isLocal?: boolean,
): LinkAction {
  const ok = !!configuredUrl(url, isLocal);
  return { enabled: ok, label: ok ? ready : pending };
}
